import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Spin, Empty, Button, Image, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    GET_POP_APPOINTMENT_DETAIL,
    GetPopAppointmentDetailResponse,
} from './graphql/queries/getPopAppointmentDetail';
import {
    GET_PICKUP_EXCEPTION_REASONS,
    GetPickupExceptionReasonsResponse,
} from '../appointment/graphql/queries/getPickupExceptionReasons';
import {
    GET_POP_DISQUALIFY_REASON_DICT,
    GetPopDisqualifyReasonDictResponse,
    LangTextEntry,
} from './graphql/queries/getPopDisqualifyReasonDict';
import SchedulePickupModal from './components/SchedulePickupModal';

/** 按当前 locale 从多语文案列表取文案，缺失回退 en/首个 */
const pickLang = (entries: LangTextEntry[] | undefined, lang: string): string => {
    if (!entries || !entries.length) return '';
    const norm = lang.startsWith('zh') ? 'zh' : lang.startsWith('es') ? 'es' : 'en';
    return entries.find((e) => e.lang === norm)?.text
        || entries.find((e) => e.lang === 'en')?.text
        || entries[0].text;
};

/** 解析 S3 URL JSON 数组，容错返回 string[] */
const parseUrls = (json?: string): string[] => {
    if (!json) return [];
    try {
        const v = JSON.parse(json);
        return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
    } catch {
        return [];
    }
};

/** 距离格式化：展示原始米(整数)，单位在标签「距站点(米)」中标注 */
const formatDistance = (m?: number): string => (m == null ? '--' : `${Math.round(m)}`);

/**
 * 揽收进度页「问题预约单详情页」(POP)。返回式页面：站点/揽收/审核 三段只读，
 * 操作区仅「安排司机上门」；下方三图片位(签收单/现场/站点)本期只读查看。
 */
interface Props {
    /** 作为浮层嵌入时传入(优先于路由参数)；不传则回退路由 :id */
    appointmentId?: number;
    /** 返回上一级；不传则回退浏览器历史 navigate(-1) */
    onBack?: () => void;
    /**
     * 是否允许「安排司机上门」。仅疑似虚假来源(fake 抽屉)传 true；
     * 揽收失败来源传 false。由上级页面标识决定，而非本条单的 reviewStatus
     * （同一条单可能同时出现在两个列表里）。
     */
    allowSchedule?: boolean;
}

const PopAppointmentDetailPage: React.FC<Props> = ({ appointmentId: propAppointmentId, onBack, allowSchedule = false }) => {
    const { id: routeId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const td = (k: string) => t(`pickup.progress.popDetail.${k}`);
    const [scheduleVisible, setScheduleVisible] = useState(false);

    const appointmentId = propAppointmentId ?? Number(routeId);
    const goBack = onBack ?? (() => navigate(-1));

    const { data, loading } = useQuery<GetPopAppointmentDetailResponse>(GET_POP_APPOINTMENT_DETAIL, {
        variables: { appointmentId },
        skip: appointmentId == null || Number.isNaN(appointmentId),
        fetchPolicy: 'network-only',
    });
    // 异常原因字典：把 exception_reason_code 映射为当前语言文案
    const { data: reasonsData } = useQuery<GetPickupExceptionReasonsResponse>(GET_PICKUP_EXCEPTION_REASONS);
    // POP 不合格原因字典：审核失败原因 code → 分类 + 文案（文案单一来源，随字典下发）
    const { data: dictData } = useQuery<GetPopDisqualifyReasonDictResponse>(GET_POP_DISQUALIFY_REASON_DICT);

    const d = data?.getPopAppointmentDetail;

    const lang = i18n.language || 'en';
    /** 审核不合格原因：codes → [{category, text}]（按当前语言） */
    const disqualifyItems = (() => {
        if (!d?.disqualifyReasonCodesJson) return [] as { category: string; text: string }[];
        let codes: string[] = [];
        try { const arr = JSON.parse(d.disqualifyReasonCodesJson); codes = Array.isArray(arr) ? arr : []; } catch { codes = []; }
        const dict = dictData?.getPopDisqualifyReasonDict || [];
        return codes.map((code) => {
            const opt = dict.find((o) => o.code === code);
            return { category: opt ? pickLang(opt.categoryTexts, lang) : '', text: opt ? pickLang(opt.texts, lang) : code };
        });
    })();

    /** 揽收失败原因 code → 当前语言文案（附 code），无字典命中则回退 code */
    const failReasonText = (code?: string): string => {
        if (!code) return '--';
        const r = (reasonsData?.getExceptionDictionaryForPickup || []).find((x) => x.exceptionReasonCode === code);
        if (!r) return code;
        const lang = i18n.language || 'en';
        const txt = lang.startsWith('zh') ? r.secondaryTypeZh : lang.startsWith('es') ? r.secondaryTypeEs : r.secondaryTypeEn;
        return txt ? `${txt} (${code})` : code;
    };

    const header = (
        <div className="flex items-center gap-2 mb-4">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack} />
            <span className="text-base font-medium">
                {d?.appointmentNumber || td('pageTitle')}
            </span>
        </div>
    );

    if (loading) {
        return <div className="bg-white rounded-lg p-6">{header}<div className="flex justify-center py-16"><Spin /></div></div>;
    }
    if (!d) {
        return <div className="bg-white rounded-lg p-6">{header}<Empty description={td('notFound')} /></div>;
    }

    const gps = (d.pickupGpsLatitude != null && d.pickupGpsLongitude != null)
        ? `${d.pickupGpsLatitude}, ${d.pickupGpsLongitude}` : '--';
    const contact = [d.contactName, d.contactPhone].filter(Boolean).join('  ') || '--';
    // 审核失败原因：分类文案 + 具体原因红标签（对齐质检审核页展示）
    const disqualifyCategories = [...new Set(disqualifyItems.map((i) => i.category).filter(Boolean))];
    const disqualifyNode: React.ReactNode = disqualifyItems.length ? (
        <div>
            {disqualifyCategories.map((c) => <div key={c} className="text-gray-800">{c}</div>)}
            <div className="flex flex-wrap gap-1 mt-1">
                {disqualifyItems.map((it, idx) => <Tag key={idx} color="red" style={{ marginInlineEnd: 0 }}>{it.text}</Tag>)}
            </div>
        </div>
    ) : '--';

    const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
        <div className="flex py-1.5 text-sm">
            <div className="text-gray-500" style={{ width: 96, flex: 'none' }}>{label}：</div>
            <div className="text-gray-800 break-all">{value ?? '--'}</div>
        </div>
    );
    const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        <div className="text-sm font-semibold mb-2">{children}</div>
    );
    const photoUrls = parseUrls(d.photosS3UrlJson);

    return (
        <div className="bg-white rounded-lg p-6">
            {header}

            {/* 顶部卡片：预约单号 + 站点/揽收/审核 三栏 */}
            <div className="border rounded-lg mb-4">
                <div className="px-4 py-2.5 border-b text-sm">
                    <span className="text-gray-500 mr-3">{td('orderNo')}</span>
                    <span style={{ color: '#6C4CE3' }}>{d.appointmentNumber || '--'}</span>
                </div>
                <div className="grid grid-cols-3 gap-6 p-4">
                    {/* 站点信息 */}
                    <div>
                        <SectionTitle>{td('sectionSite')}</SectionTitle>
                        <Row label={td('site')} value={d.pickupSiteName || d.pickupSiteId || '--'} />
                        <Row label={td('siteAddress')} value={d.siteAddress || '--'} />
                        <Row label={td('contact')} value={contact} />
                    </div>
                    {/* 揽收信息 */}
                    <div>
                        <SectionTitle>{td('sectionPickup')}</SectionTitle>
                        <Row label={td('failReason')} value={failReasonText(d.exceptionReasonCode)} />
                        <Row label={td('pickupLocation')} value={gps} />
                        <Row label={td('distance')} value={formatDistance(d.distanceMeters)} />
                    </div>
                    {/* 审核信息 + 操作(仅安排司机上门) */}
                    <div>
                        <div className="flex items-start justify-between">
                            <SectionTitle>{td('sectionReview')}</SectionTitle>
                            {/* 仅疑似虚假来源(fake 抽屉)才可复揽；揽收失败来源不展示。
                                由上级传入 allowSchedule 决定，不看本单 reviewStatus。 */}
                            {allowSchedule && (
                                <Button type="primary" onClick={() => setScheduleVisible(true)}>
                                    {t('pickup.progress.qualityFeedback.schedule')}
                                </Button>
                            )}
                        </div>
                        <Row label={td('reviewer')} value={d.reviewerName || '--'} />
                        <Row label={td('disqualifyReason')} value={disqualifyNode} />
                        <Row label={td('remark')} value={d.reviewRemark || '--'} />
                    </div>
                </div>
            </div>

            {/* POP 照片（现场取证）：布局对齐质检侧 POP 审核详情——9:16 竖图卡片、≤3 张占满高度 */}
            {photoUrls.length ? (
                <div
                    className={`grid gap-4 ${
                        photoUrls.length <= 3
                            ? 'grid-cols-3 h-[75vh]'
                            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 max-h-[75vh] overflow-y-auto'
                    }`}
                >
                    <Image.PreviewGroup>
                        {photoUrls.map((u, i) => (
                            <div
                                key={i}
                                className={`aspect-[9/16] border border-gray-300 rounded-lg overflow-hidden hover:border-purple-400 transition-colors bg-white ${
                                    photoUrls.length <= 3 ? 'h-full' : 'max-h-[75vh] mx-auto'
                                }`}
                            >
                                <Image src={u} alt={`POP ${i + 1}`} className="object-cover w-full h-full" />
                            </div>
                        ))}
                    </Image.PreviewGroup>
                </div>
            ) : (
                <div className="text-xs text-gray-400">{td('noPhoto')}</div>
            )}

            <SchedulePickupModal
                visible={scheduleVisible}
                onClose={() => setScheduleVisible(false)}
                target={{ pickupSiteId: d.pickupSiteId, address: d.siteAddress, channelId: d.channelId, appointmentId: d.appointmentId }}
                baseDate={d.expectedPickupDate || dayjs().format('YYYY-MM-DD')}
            />
        </div>
    );
};

export default PopAppointmentDetailPage;
